import '@/utils/perf-install';

// Supabase Auth loads WebAuthn for passkeys; in some environments (extensions, iframes, strict CSP)
// navigator.credentials is read-only and assigning to .get throws. Catch so the app keeps running.
if (typeof window !== 'undefined') {
  const isWebAuthnCredentialsError = (msg: string) =>
    typeof msg === 'string' && msg.includes('CredentialsContainer') && msg.includes("read only property 'get'");
  window.addEventListener('error', (event) => {
    if (isWebAuthnCredentialsError(event.message)) {
      console.warn('[Harmonia] WebAuthn credentials init skipped (safe to ignore):', event.message);
      event.preventDefault();
      event.stopImmediatePropagation();
      return true;
    }
  });
  window.addEventListener('unhandledrejection', (event) => {
    const msg = event.reason?.message ?? String(event.reason ?? '');
    if (isWebAuthnCredentialsError(msg)) {
      console.warn('[Harmonia] WebAuthn credentials init skipped (safe to ignore):', msg);
      event.preventDefault();
    }
  });
}

import { createApp } from 'vue';
import { createPinia } from 'pinia';
import * as Sentry from '@sentry/capacitor';
import * as SentryVue from '@sentry/vue';
import App from './App.vue';
import router from './router';
import directives from './directives';
import { i18n, detectLocale, loadLocale } from './i18n';
import { toast } from 'vue-sonner';
import { perfMark, perfLogSummary } from '@/utils/perf-metrics';
import { notifyAppResumed } from '@/services/app-resume.service';

// Import global styles — index.css handles Ionic+Tailwind cascade layering.
// See the @layer declaration at the top of index.css.
import 'vue-sonner/style.css';
import './index.css';
import './assets/mobile-viewport.css';
import './assets/keyboard.css';


perfMark('main-start');

// Fallback: filter Swiper FreeMode emit warning from console (in case warnHandler isn't used, e.g. different Vue instance)
if (typeof window !== 'undefined') {
  const origWarn = console.warn;
  console.warn = function (...args: unknown[]) {
    const first = args[0];
    const s = typeof first === 'string' ? first : String(first ?? '');
    if (s.includes('_freeModeStaticRelease') && s.includes('emitted event')) return;
    origWarn.apply(console, args);
  };
}

// App creation moved into bootstrap() below (allows blocking locale preload before mount)

// Fonction pour effectuer un reload avec transition
const performReloadWithTransition = () => {
  // Créer l'animation SVG
  const spinner = document.createElement('div');
  spinner.className = 'loading-spinner';
  spinner.innerHTML = `
    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="12" cy="12" r="10" stroke="#e5e7eb" stroke-width="2"/>
      <path d="M12 2a10 10 0 0 1 10 10" stroke="#10b981" stroke-width="2" stroke-linecap="round">
        <animateTransform
          attributeName="transform"
          type="rotate"
          values="0 12 12;360 12 12"
          dur="1s"
          repeatCount="indefinite"
        />
      </path>
    </svg>
  `;

  // Ajouter l'animation au body
  document.body.appendChild(spinner);

  // Ajouter une classe CSS pour l'overlay
  document.body.classList.add('page-reloading');

  // Attendre un peu pour que l'animation soit visible
  setTimeout(() => {
    window.location.reload();
  }, 800);
};

// Fonction pour afficher une notification avant le reload
const showReloadNotification = () => {
  // Utiliser le toast Sonner de l'app
  toast.info('Mise à jour des données...', {
    duration: 1000,
    position: 'top-center',
  });

  // Reload après 1 seconde
  setTimeout(() => {
    performReloadWithTransition();
  }, 1000);
};

// Variables simples pour le reload
let appWasBlurred = false;

// Option pour afficher une notification avant le reload
const SHOW_RELOAD_NOTIFICATION = false; // true = notification, false = reload direct

// Flag pour utiliser le reload forcé au lieu de la récupération de contexte
const USE_FORCE_RELOAD = false;

// Écouter les événements de blur de la fenêtre
window.addEventListener('blur', () => {
  // console.log('💤 [main.ts] Window blurred');
  appWasBlurred = true;
});

// Écouter les changements de visibilité
document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    // console.log('👁️ [main.ts] App hidden');
    appWasBlurred = true;
  }
});

// Écouter les événements de focus de la fenêtre
window.addEventListener('focus', () => {
  // console.log('🎯 [main.ts] Window focused');

  // Si l'app était en blur, récupérer le contexte ou reload
  if (appWasBlurred) {
    // console.log('🔄 [main.ts] App was blurred, recovering context...');
    appWasBlurred = false;

    if (USE_FORCE_RELOAD) {
      if (SHOW_RELOAD_NOTIFICATION) {
        showReloadNotification();
      } else {
        performReloadWithTransition();
      }
    } else {
      // Point d'entrée unique du resume (contexte + refreshes + app-resumed),
      // dédupliqué avec le handler natif appStateChange (App.vue) qui tire
      // aussi sur device.
      notifyAppResumed();
    }
  }
});

async function bootstrap() {
  const initialLocale = detectLocale();

  // Blocking preload to avoid EN flash on first launch for non-EN users
  if (initialLocale !== 'en') {
    try {
      await loadLocale(initialLocale);
      // Cast via string for vue-i18n which types locale narrowly from initial messages
      (i18n.global.locale as unknown as { value: string }).value = initialLocale;
      document.querySelector('html')?.setAttribute('lang', initialLocale);
    } catch (err) {
      console.warn('[main] locale preload failed, continuing in en:', err);
    }
  } else {
    document.querySelector('html')?.setAttribute('lang', 'en');
  }

  const pinia = createPinia();
  const app = createApp(App)
    .use(pinia)
    .use(router)
    .use(directives)
    .use(i18n);

  // Sentry.init is deferred to just after mount (see initSentry below) so its
  // integration setup and browser-tracing instrumentation don't run on the main
  // thread before the first frame. Error/warn capture is attached to the app
  // instance and stays live for the whole session.
  const initSentry = () => {
    Sentry.init(
      {
        dsn: 'https://530ed69f2a2b47b7d50b4c88674749a5@o4507408711155712.ingest.de.sentry.io/4511475405488208',
        environment: import.meta.env.MODE || 'development',
        release: import.meta.env.VITE_APP_VERSION || '0.0.0',
        sendDefaultPii: false,
        integrations: [SentryVue.browserTracingIntegration({ router })],
        tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
        tracePropagationTargets: ['localhost', /^https:\/\/jvlujbqgxdeloslojxnj\.supabase\.co/],
        enableLogs: true,
        // PWA Service Worker registration can fail for reasons outside our control
        // (stale clients on old releases, browsers blocking SW, network hiccups
        // fetching sw.js). These surface as uncatchable rejections from the
        // vite-plugin-pwa registerSW.js shim and are pure noise — drop them.
        // See Sentry HARMONIA-2 and HARMONIA-8.
        ignoreErrors: [
          /Failed to register a ServiceWorker/i,
          /ServiceWorker.*sw\.js/i,
        ],
        // Chaque breadcrumb devient un appel de pont Capacitor vers le natif
        // (SentryCapacitor.addBreadcrumb), donc une sérialisation JSON + un
        // aller-retour WKWebView. L'app journalise beaucoup en info/debug
        // (« Already syncing this household, skipping », résumés de perf…), et
        // sur device ce bavardage tombe pile pendant les bascules d'onglet.
        // On garde warn/error, qui sont ce qui sert vraiment au diagnostic,
        // et on jette le reste des breadcrumbs console en production.
        beforeBreadcrumb(breadcrumb) {
          if (
            import.meta.env.PROD &&
            breadcrumb.category === 'console' &&
            breadcrumb.level !== 'warning' &&
            breadcrumb.level !== 'error'
          ) {
            return null;
          }
          return breadcrumb;
        },
        beforeSend(event) {
          const frames = event.exception?.values?.flatMap(
            (v) => v.stacktrace?.frames ?? []
          ) ?? [];
          const fromSwRegistration = frames.some((f) => {
            const where = `${f.filename ?? ''} ${f.function ?? ''}`;
            return /registerSW\.js/i.test(where) ||
              /serviceWorker.*register/i.test(where) ||
              /ServiceWorkerContainer\.register/i.test(where);
          });
          if (fromSwRegistration) return null;
          return event;
        },
        siblingOptions: {
          vueOptions: {
            app,
            attachProps: true,
            attachErrorHandler: true,
          },
        },
      },
      SentryVue.init
    );
  };

  app.config.warnHandler = (msg, _instance, trace) => {
    const s = typeof msg === 'string' ? msg : String(msg);
    if (s.includes('_freeModeStaticRelease') || (s.includes('freeMode') && s.includes('emitted event'))) return;
    console.warn('[Vue warn]:', msg, trace);
  };

  // Persist to localStorage (setLocale does this but we bypassed it above for blocking semantics)
  try { localStorage.setItem('user-locale', initialLocale); } catch { /* noop */ }

  perfMark('main-before-mount');
  app.mount('#app');
  perfMark('main-after-mount');
  perfLogSummary('main-mount-complete');

  // Post-mount, off the critical path: wire up Sentry so it captures the rest
  // of the session. Amplitude is initialized from App.vue's onMounted instead
  // (via useAnalytics().initialize(), see that composable) — its SDK init and
  // its user-property Identify must happen in the same synchronous flow, before
  // any track() call, so they can no longer live in two places that race against
  // each other.
  const initDeferredSdks = () => {
    initSentry();
  };
  if (typeof requestIdleCallback !== 'undefined') {
    requestIdleCallback(() => initDeferredSdks());
  } else {
    setTimeout(() => initDeferredSdks(), 0);
  }
}

bootstrap();
